import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { aboutImage, portraitImage, heroImage } from "../data/siteData";

const About = () => {
  return (
    <div data-testid="about-page" className="bg-[#0a0a0a] pt-24 pb-10 lg:pt-28 lg:pb-16">
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 mb-10 lg:mb-20">
        <p className="text-[var(--accent)] text-xs tracking-[0.32em] uppercase mb-5">Who We Are</p>
        <h1 className="font-display text-white text-[clamp(56px,10vw,180px)] leading-[0.88]">
          A movement.
          <br />
          <span className="font-elegant italic font-light">Not a spa.</span>
        </h1>
      </section>

      {/* Story */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-14 lg:mb-32">
        <div className="lg:col-span-5">
          <div className="tile aspect-[3/4]">
            <img src={portraitImage} alt="Society ritual" loading="lazy" />
          </div>
        </div>
        <div className="lg:col-span-7">
          <h2 className="font-display text-white text-[clamp(36px,5vw,80px)] leading-[0.95]">
            Born in Ambridge.
            <br />
            <span className="font-elegant italic font-light">Built for the bold.</span>
          </h2>
          <div className="mt-5 lg:mt-8 space-y-4 lg:space-y-5 text-white/75 leading-relaxed text-[15px]">
            <p>
              Welcome to Society — Ambridge's destination for edgy luxury and elevated
              self-care. We're redefining the day spa experience with a bold, vibey
              atmosphere where relaxation meets rebellion. At Society, beauty is a
              ritual, and every service is designed to leave you feeling powerful,
              radiant, and unapologetically you.
            </p>
            <p>
              Society isn't your typical day spa — we're a movement. Our space is raw,
              vibey, and fearless, where self-care becomes a statement and every
              treatment is a ritual of rebellion. Welcome to Society — where you don't
              just glow, you ignite.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 mb-14 lg:mb-32">
        <h2 className="font-display text-white text-[clamp(40px,6vw,100px)] leading-[0.92] max-w-4xl">
          What we
          <span className="font-elegant italic font-light"> stand </span>
          for.
        </h2>
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 mt-8 lg:mt-14">
          {[
            { t: "Bold by Design", d: "We refuse generic. Every service is intentional, every ritual unforgettable." },
            { t: "Luxury Unfiltered", d: "Premium products, premium care — without the stiff, gatekept feel of traditional spas." },
            { t: "You, Ignited", d: "We don't just treat skin. We empower confidence and unlock your most magnetic self." },
          ].map((v, i) => (
            <div key={v.t} className="svc-card" data-testid={`value-${i}`}>
              <p className="font-display text-[var(--accent)] text-5xl mb-5">0{i + 1}</p>
              <h3 className="font-display text-white text-2xl mb-3">{v.t}</h3>
              <p className="text-white/65 text-sm leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA — cinematic editorial (matches Home) */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="relative rounded-[20px] overflow-hidden border border-white/5">
          {/* Background image */}
          <img
            src="/portfolio/cta-model.jpg"
            alt="Society — bold beauty"
            className="absolute inset-0 w-full h-full object-cover scale-105"
            loading="lazy"
          />
          {/* Layered overlays for cinematic depth */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/65 to-[#0a0a0a]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/40" />
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(60% 80% at 80% 50%, rgba(243,168,92,0.18), transparent 70%)" }}
          />

          {/* Giant ghost gold logo watermark */}
          <img
            src="/brand/society-gold.png"
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-12 lg:right-8 top-1/2 -translate-y-1/2 w-[420px] lg:w-[560px] opacity-15 lg:opacity-25 select-none"
            style={{ filter: "drop-shadow(0 0 80px rgba(243,168,92,0.35))" }}
          />

          {/* Inner content */}
          <div className="relative px-8 sm:px-12 lg:px-16 py-12 lg:py-28 min-h-[340px] lg:min-h-[560px] flex items-center">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-7">
                <span className="w-10 h-px bg-[var(--warm-gold)]" />
                <span className="text-[10px] tracking-[0.36em] uppercase text-[var(--warm-gold)] font-medium">
                  Your Invitation
                </span>
              </div>

              <h3 className="font-display text-white text-[clamp(48px,8vw,130px)] leading-[0.88]">
                Ready to
                <span className="block font-elegant italic font-light text-white/95">ignite?</span>
              </h3>

              <Link to="/bookings" className="btn-accent mt-7 lg:mt-10" data-testid="about-cta-book">
                Book Your Ritual <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
